// Identifies the Upsales Chrome side-panel context specifically — NOT just "in an
// iframe" (which would trap unrelated same-origin iframes: portals, E2E, future embeds).
//
// Primary signal: window.location.ancestorOrigins — the origin of the framing context.
// In the side panel that's our extension's origin. It's reliable in Chromium (our only
// target), reflects the true frame ancestry, and survives the post-login navigation
// (the hash changes, the ancestry doesn't). Falls back to the `?upsalesExtension=1`
// marker the panel sets (persisted to sessionStorage) for any context where
// ancestorOrigins is unavailable. WEB-7101.
// The Chrome Web Store assigns its own id (the published build has no manifest `key`); the
// local unpacked build keeps the key-derived id. Accept both so the panel works whether it's
// installed from the store or loaded unpacked during development.
export const EXTENSION_ORIGINS = [
	'chrome-extension://gmiblhpneimhpkjcgkmfgfghbpioimjc', // Chrome Web Store build
	'chrome-extension://mecbjghoagdkfjecfokjgffhaifogfmi' // local unpacked (manifest key)
];
const MARKER = 'upsalesExtension';

export const isExtensionPanel = (): boolean => {
	try {
		const ancestors = window.location.ancestorOrigins;
		if (ancestors && EXTENSION_ORIGINS.some(origin => ancestors.contains(origin))) {
			return true;
		}
	} catch {
		// ancestorOrigins unavailable — fall through to the marker.
	}
	try {
		if (new URLSearchParams(window.location.search).get(MARKER) === '1') {
			window.sessionStorage.setItem(MARKER, '1');
		}
		return window.sessionStorage.getItem(MARKER) === '1';
	} catch {
		return false;
	}
};

// SSO login can't run inside the panel iframe — the identity provider forbids being framed.
// Hand the IdP URL to the panel, which opens it in a top-level tab and reloads the iframe once
// the session cookie is set. Target the framing extension origin specifically; framing is already
// locked to the extension by the edge frame-ancestors header, so '*' is a safe last resort (the
// URL is a public SSO endpoint). WEB-7331.
export const postSsoLoginToPanel = (url: string): void => {
	let target = '*';
	try {
		const ancestors = window.location.ancestorOrigins;
		const origin = ancestors && EXTENSION_ORIGINS.find(o => ancestors.contains(o));
		if (origin) {
			target = origin;
		}
	} catch {
		// ancestorOrigins unavailable — fall back to '*'.
	}
	window.parent.postMessage({ type: 'sso-login', url }, target);
};
